import { House, Trees } from "lucide-react";
import { useDesign } from "../../lib/store";
import { roomSetting } from "../../lib/finishes/effective";
import { trackRoomSettingChange } from "../../lib/finishesTelemetry";
import { OUTDOOR_ROOMS_FLAG, ROOM_FINISHES_FLAG, useFlag } from "../../lib/flags";
import type { RoomSetting } from "../../lib/types";
import { Segmented } from "../ui/Segmented";

/**
 * Room details' Indoor / Outdoor switch. Outdoors the walls become fences,
 * hedges or the neighbor's house and the ceiling becomes sky; the shape,
 * furniture and openings stay where they are.
 */
export function RoomSettingControl() {
  const finishesOn = useFlag(ROOM_FINISHES_FLAG);
  const outdoorOn = useFlag(OUTDOOR_ROOMS_FLAG);
  const finishes = useDesign((s) => s.design.room.finishes);
  const setRoomSetting = useDesign((s) => s.setRoomSetting);
  if (!finishesOn || !outdoorOn) return null;
  const setting = roomSetting(finishes);

  function pick(next: RoomSetting) {
    if (next === setting) return;
    const { design: before, activeSharedRoomId } = useDesign.getState();
    setRoomSetting(next);
    trackRoomSettingChange({ to: next, before, sharedRoomId: activeSharedRoomId, from: { source: "shape", view: "build" } });
  }

  return (
    <div className="mb-5" data-testid="room-setting">
      <Segmented
        size="sm"
        className="w-full max-w-sm [&>button]:flex-1"
        value={setting}
        onChange={pick}
        options={[
          { id: "indoor", label: "Indoor", icon: <House className="size-3.5" aria-hidden /> },
          { id: "outdoor", label: "Outdoor", icon: <Trees className="size-3.5" aria-hidden /> },
        ]}
      />
      <p className="mt-1.5 text-caption text-ink-400">
        {setting === "outdoor" ? "Fences, hedges and open sky instead of walls and a ceiling." : "A yard or patio? Switch to Outdoor."}
      </p>
    </div>
  );
}
